import { getCartQuantityForVariant } from '@lib/data/cart';
import { getVariantAvailability } from '@lib/data/variants';

export type VariantAvailabilityInfo = {
  /** `undefined` means "no stock-based ceiling applies" (not inventory-managed, or backorder allowed). */
  remaining: number | undefined;
  /** Whether the variant can be purchased at all, independent of quantity already in the basket. */
  inStock: boolean;
};

/**
 * Live, uncached (see `getVariantAvailability`) read of a variant's
 * availability. Both derived fields come from the same fetch so a restock —
 * or a stale, globally-cached `manage_inventory`/`inventory_quantity` on the
 * page's own render — is reflected immediately, without waiting for the
 * variant's `isVariantInStock` snapshot to fall out of the page's cache TTL.
 */
export async function fetchVariantAvailability(
  variantId: string,
): Promise<VariantAvailabilityInfo> {
  const [availability, alreadyInCart] = await Promise.all([
    getVariantAvailability(variantId),
    getCartQuantityForVariant(variantId),
  ]);

  if (!availability?.manageInventory || availability.allowBackorder) {
    return { remaining: undefined, inStock: true };
  }

  const quantity = availability.inventoryQuantity ?? 0;

  return {
    remaining: Math.max(0, quantity - alreadyInCart),
    inStock: quantity > 0,
  };
}

/** `remaining === undefined` means "no stock-based ceiling applies". */
export function isStockBlocked(
  remaining: number | undefined,
  requestedQuantity: number,
): boolean {
  return remaining !== undefined && requestedQuantity > remaining;
}
